import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../../../../supabaseClient';

const WEEKDAY_LABELS = ['PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB', 'NIE'];
const WEEKDAY_MONDAY_INDEX = (jsDay) => (jsDay + 6) % 7;

function toISODate(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function generateMonthGrid(year, month) {
  const firstOfMonth = new Date(year, month - 1, 1);
  const lastOfMonth = new Date(year, month, 0);
  const leading = WEEKDAY_MONDAY_INDEX(firstOfMonth.getDay());
  const daysInMonth = lastOfMonth.getDate();
  const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;
  const gridStart = new Date(year, month - 1, 1 - leading);
  const days = [];
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    days.push({ dateISO: toISODate(d), dayNum: d.getDate(), isCurrentMonth: d.getMonth() === month - 1 });
  }
  return days;
}

async function fetchCalendarEvents(startDate, endDate, idConfig) {
  const { data, error } = await supabase
    .from('calendar_events')
    .select(`
      id, title, event_date, event_time, notes,
      category:calendar_categories ( id, name, color ),
      assigned_profile:profiles!calendar_events_assigned_to_fkey ( id, full_name )
    `)
    .eq('id_configuration', idConfig)
    .gte('event_date', startDate)
    .lte('event_date', endDate)
    .order('event_date', { ascending: true })
    .order('event_time', { ascending: true });
  if (error) throw error;
  return data;
}

function CalendarMonth({ currentDate, filterCategory, filterEmployee, onAddClick, onEventClick, eventsVersion, idConfig }) {
  const [year, month] = currentDate.split('-').map(Number).slice(0, 2);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popupDate, setPopupDate] = useState(null);

  const days = useMemo(() => generateMonthGrid(year, month), [year, month]);

  useEffect(() => {
    if (!idConfig) return;
    let cancelled = false;
    setLoading(true);
    fetchCalendarEvents(days[0].dateISO, days[days.length - 1].dateISO, idConfig)
      .then((data) => { if (!cancelled) setEvents(data); })
      .catch((err) => console.error('Błąd pobierania akcji kalendarza:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, eventsVersion, idConfig]);

  const filteredEvents = useMemo(() => events.filter((ev) =>
    (filterCategory === 'ALL' || ev.category?.id === filterCategory) &&
    (filterEmployee === 'ALL' || ev.assigned_profile?.id === filterEmployee)
  ), [events, filterCategory, filterEmployee]);

  const eventsByDate = useMemo(() => filteredEvents.reduce((acc, ev) => {
    (acc[ev.event_date] ||= []).push(ev);
    return acc;
  }, {}), [filteredEvents]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const popupEvents = popupDate ? (eventsByDate[popupDate] || []) : [];

  return (
    <div style={{ position: 'relative', background: '#fff', border: '1px solid #eee', borderRadius: 20, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f0f0f0' }}>
        {WEEKDAY_LABELS.map((wd) => (
          <div key={wd} style={{ padding: '10px 8px', fontSize: 12, fontWeight: 700, color: '#705A5E', textAlign: 'center' }}>{wd}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((day) => {
          const allDayEvents = eventsByDate[day.dateISO] || [];
          const visibleEvents = allDayEvents.slice(0, 3);
          const extraCount = allDayEvents.length - 3;
          const isToday = day.dateISO === todayISO;
          return (
            <div
              key={day.dateISO}
              onClick={() => onAddClick?.(day.dateISO)}
              style={{
                minHeight: 104, padding: 6, borderRight: '1px solid #f5f0f0', borderBottom: '1px solid #f5f0f0',
                background: day.isCurrentMonth ? '#fff' : '#fdf9f6', cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span style={{
                  fontSize: 12, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', fontWeight: isToday ? 800 : 500,
                  color: isToday ? '#fff' : (day.isCurrentMonth ? '#2C1419' : '#c9bcbc'),
                  background: isToday ? '#6B1D2F' : 'transparent',
                }}>{day.dayNum}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                {visibleEvents.map((ev) => (
                  <div
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                    style={{
                      background: ev.category?.color || '#999', color: '#fff', fontSize: 11, fontWeight: 600,
                      padding: '3px 6px', borderRadius: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {ev.event_time?.slice(0, 5)} {ev.title}
                  </div>
                ))}
                {extraCount > 0 && (
                  <div
                    onClick={(e) => { e.stopPropagation(); setPopupDate(day.dateISO); }}
                    style={{ fontSize: 10, color: '#705A5E', fontWeight: 600, paddingLeft: 4, cursor: 'pointer' }}
                  >
                    +{extraCount} więcej
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {loading && <div style={{ padding: 12, fontSize: 12, color: '#705A5E' }}>Ładowanie…</div>}

      {popupDate && (
        <div onClick={() => setPopupDate(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(44,20,25,0.35)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, padding: 20, width: 320, maxWidth: '90vw',
            maxHeight: '70vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#6B1D2F', marginBottom: 12 }}>{popupDate}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {popupEvents.map((ev) => (
                <div
                  key={ev.id}
                  onClick={() => { onEventClick?.(ev); setPopupDate(null); }}
                  style={{
                    background: (ev.category?.color || '#999') + '17', borderLeft: `3px solid ${ev.category?.color || '#999'}`,
                    borderRadius: 6, padding: '6px 8px', cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#2C1419' }}>{ev.event_time?.slice(0, 5)} · {ev.title}</div>
                  <div style={{ fontSize: 11, color: '#705A5E' }}>{ev.assigned_profile?.full_name || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarMonth;